'use client'

import { AlertTriangleIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { exchangeDisplayName, formatDuration } from '@/components/widget/market-status-badge'
import { cn } from '@/lib/utils'
import type { VenueTradingIssue } from '@/service/venue-trading-status.hook'

const MESSAGE_KEY = {
  disabled: 'disabled',
  'trading-down': 'tradingDown',
  outage: 'outage',
  maintenance: 'maintenance',
} as const

/**
 * Says that orders on these venues will fail (or likely will) — so the user learns it from the
 * connection status instead of from rejected orders, again and again. One line per venue; red
 * when routing is off, gold when it's a warning.
 */
export function VenueTradingNotice({
  issues,
  className,
}: {
  issues: readonly VenueTradingIssue[]
  className?: string
}) {
  const t = useTranslations('venueTradingNotice')
  if (issues.length === 0) return null
  return (
    <div role="status" className={cn('flex flex-col gap-1', className)}>
      {issues.map((issue) => (
        <p
          key={issue.exchange}
          className={cn(
            'flex items-start gap-1.5 text-xs tracking-tight',
            issue.blocksOrders ? 'text-chart-red' : 'text-ox-gold'
          )}
        >
          <AlertTriangleIcon size={13} className="mt-px shrink-0" aria-hidden="true" />
          <span>
            {t(MESSAGE_KEY[issue.kind], { exchange: exchangeDisplayName(issue.exchange) })}
            {issue.sinceMs !== null && (
              <span className="opacity-70">
                {' '}
                {t('since', { duration: formatDuration(Date.now() - issue.sinceMs) })}
              </span>
            )}
          </span>
        </p>
      ))}
    </div>
  )
}
